import { PUBLIC_POSTHOG_KEY } from "@/config";

import { type BrowseArrival, browseArrival } from "@/lib/browse/browseArrival";
import { initAnonymousPostHog } from "@/lib/browse/publicPostHog.web";
import {
  createShowAnalytics,
  getShowAnalyticsConfig,
  SHOW_ANALYTICS_KEY,
  type ShowIdentityStore,
} from "@/lib/browse/showAnalytics";
import { siteCookie } from "@/lib/platform/siteStorage.web";

const COUNTRY_HEADER = "X-Trovie-Country";
let memoryIdentity: { id: string; showId: string; } | undefined;

/**
 * The Show identity's store: the site-scoped cookie, not this origin's `localStorage` (ADR-0106).
 *
 * The two steps ADR-0061 joins are served from two hosts, so with a per-origin store the id
 * written on the Show page is never the id read at the table. Uses `siteCookie` from
 * `siteStorage.web` because the config decides synchronously; building this object writes nothing,
 * which lets the EEA branch bypass the store entirely.
 */
const identityStore: ShowIdentityStore = {
  read: () => siteCookie.read(SHOW_ANALYTICS_KEY),
  write: (value, maxAgeSeconds) => siteCookie.write(SHOW_ANALYTICS_KEY, value, maxAgeSeconds),
  clear: () => siteCookie.clear(SHOW_ANALYTICS_KEY),
};

function disabledAnalytics() {
  return createShowAnalytics({ capture() {} });
}

export async function loadShowAnalytics(input: {
  showId: string | null;
  showEndsAt: string | null;
}) {
  if (!PUBLIC_POSTHOG_KEY) return disabledAnalytics();

  await new Promise<void>((resolve) => requestAnimationFrame(() => resolve()));

  try {
    const countryResponse = await fetch(window.location.href, {
      cache: "no-store",
      method: "HEAD",
    });
    const config = getShowAnalyticsConfig({
      country: countryResponse.headers.get(COUNTRY_HEADER),
      showId: input.showId,
      showEndsAt: input.showEndsAt,
      now: Date.now(),
      store: identityStore,
      createId: () =>
        memoryIdentity && (input.showId === null || memoryIdentity.showId === input.showId)
          ? memoryIdentity.id
          : window.crypto.randomUUID(),
    });
    // Unconditional: every branch of the config keeps the provider's own state in memory, so the
    // id is always worth carrying for the life of this document. Where the store refused the
    // write, this is the only thing that keeps that visitor's events joinable.
    memoryIdentity = {
      id: config.bootstrap.distinctID,
      showId: input.showId ?? memoryIdentity?.showId ?? "",
    };

    const instance = await initAnonymousPostHog(config);

    return createShowAnalytics({
      capture(event, properties) {
        instance.capture(event, properties);
      },
    });
  } catch {
    return disabledAnalytics();
  }
}

/**
 * How this page was reached, from the browser's own two signals (TRO-644).
 *
 * Read here rather than in the screen because `document` does not exist on native, and the screen
 * is shared. Neither value is ever sent: `browseArrival` reduces both to one enum, and the
 * referrer URL and the click-id token stay in the browser.
 */
export function currentBrowseArrival(): BrowseArrival {
  try {
    return browseArrival({ referrer: document.referrer, url: window.location.href });
  } catch {
    // A hardened browser can make either accessor throw. An unknown arrival is "direct", the same
    // bucket a missing referrer already lands in, rather than a crash on a page with no error
    // boundary.
    return "direct";
  }
}
